import { DEFAULT_CAMERA_SETTINGS, parseCameraSettings, validateCameraSettings, type CameraSettings } from './CameraSettings';

const STORAGE_KEY = 'rl-web.camera-settings.v1';
const STORAGE_VERSION = 1;

export class CameraSettingsStore {
  #settings: CameraSettings;
  #storage: Storage | null;

  constructor(storage: Storage | null = browserStorage()) {
    this.#storage = storage;
    this.#settings = loadSettings(storage);
  }

  get settings(): CameraSettings { return this.#settings; }

  set<K extends keyof CameraSettings>(key: K, value: CameraSettings[K]): boolean {
    if (!Object.hasOwn(DEFAULT_CAMERA_SETTINGS, key)) throw new TypeError(`Unknown camera setting: ${key}.`);
    const next = { ...this.#settings, [key]: value };
    validateCameraSettings(next);
    this.#settings = Object.freeze(next);
    return this.#persist();
  }

  reset(): boolean {
    this.#settings = DEFAULT_CAMERA_SETTINGS;
    return this.#persist();
  }

  #persist(): boolean {
    if (!this.#storage) return false;
    try {
      this.#storage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, settings: this.#settings }));
      return true;
    } catch {
      this.#storage = null;
      return false;
    }
  }
}

function browserStorage(): Storage | null {
  try { return globalThis.localStorage ?? null; }
  catch { return null; }
}

function loadSettings(storage: Storage | null): CameraSettings {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CAMERA_SETTINGS;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)
      || !('version' in parsed) || parsed.version !== STORAGE_VERSION || !('settings' in parsed)) {
      return DEFAULT_CAMERA_SETTINGS;
    }
    return parseCameraSettings(parsed.settings);
  } catch {
    return DEFAULT_CAMERA_SETTINGS;
  }
}
